import React, { useState } from 'react';
import { Users, Activity, XCircle, ChevronDown } from 'lucide-react';
import GlossaryLink from './GlossaryLink';

/**
 * RiskTable Component
 * Displays numbers at risk, censored, and events for Kaplan-Meier survival curves
 * 
 * @param {Object} props
 * @param {Array} props.timePoints - Array of time points (e.g., [0, 3, 6, 9, 12, 15, 18, 24])
 * @param {Array} props.cohorts - Array of cohort data objects
 * @param {string} props.timeUnit - Unit of time ('months' or 'days')
 * @param {boolean} props.showTable - Whether to display the table
 */
const RiskTable = ({
  timePoints = [0, 3, 6, 9, 12, 15, 18, 24],
  cohorts = [],
  timeUnit = 'months',
  showTable = true,
  showTimeFooter = false
}) => {
  const [isOpen, setIsOpen] = useState(false);

  if (!showTable || !cohorts || cohorts.length === 0) {
    return null;
  }

  // Filter only visible cohorts
  const visibleCohorts = cohorts.filter(cohort => cohort.visible !== false);

  if (visibleCohorts.length === 0) {
    return null;
  }

  return (
    <div className="mt-6 bg-white rounded-xl border border-slate-200 overflow-hidden">
      {/* Header - Accordion Toggle */}
      <button
        onClick={() => setIsOpen(prev => !prev)}
        className="w-full bg-gradient-to-r from-slate-50 to-slate-100 px-4 py-3 text-left focus:outline-none"
      >
        <div className='flex items-center justify-between'>
          <div className="flex flex-col items-start justify-between">
            <div className="flex items-center gap-2">
              {/* <Users className="w-5 h-5 text-slate-600" /> */}
              <h3 className="text-base font-bold text-slate-800 uppercase tracking-wide">
                Risk Table <GlossaryLink section="risk" tooltip={`At Risk: The number of patients "at risk" of having the event at each timepoint — i.e., still under observation and hasn't had the event yet (progression, death, etc.) or been censored before that point.\n\nCensored: The number of patients who have not yet reached the given timepoint as of the data cutoff.\n\nEvent: The number of patients who experienced the event (progression, death) by the given timepoint.`} />
              </h3>
            </div>
            <p className="text-xs text-slate-600 mt-1">
              Patient counts at each time point for survival analysis
            </p>
          </div>

          <ChevronDown
            className={`w-5 h-5 text-slate-500 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}
          />
        </div>
      </button>

      {/* Collapsible Content */}
      <div
        className="overflow-hidden transition-all duration-300 ease-in-out"
        style={{ maxHeight: isOpen ? '2000px' : '0px', opacity: isOpen ? 1 : 0 }}
      >
        {/* Table Container - Horizontal Scroll for Mobile */}
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-slate-50 border-b border-slate-200">
                <th className="px-4 py-3 text-left text-xs font-bold text-slate-600 uppercase tracking-wider sticky left-0 bg-slate-50 z-10">
                  Month
                </th>
                {timePoints.map((time, idx) => (
                  <th
                    key={idx}
                    className="px-3 py-3 text-center text-xs font-bold text-slate-600 uppercase tracking-wider min-w-[60px]"
                  >
                    {time}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {visibleCohorts.map((cohort, cohortIdx) => (
                <React.Fragment key={cohort.id || cohortIdx}>
                  {/* Cohort Header Row */}
                  <tr className="bg-slate-50 border-t-2 border-slate-300">
                    <td
                      colSpan={timePoints.length + 1}
                      className="px-4 py-2 font-bold text-slate-800 sticky left-0 bg-slate-50 z-10"
                    >
                      <div className="flex items-center gap-2">
                        <div
                          className="w-3 h-3 rounded-full flex-shrink-0"
                          style={{ backgroundColor: cohort.color }}
                        />
                        <span className="truncate">{cohort.name}</span>
                      </div>
                    </td>
                  </tr>

                  {/* At Risk Row */}
                  <tr className="border-b border-slate-100 hover:bg-blue-50 transition-colors">
                    <td className="px-4 py-2 text-slate-700 font-medium sticky left-0 bg-white hover:bg-blue-50 z-10">
                      <div className="flex items-center gap-2">
                        <Users className="w-4 h-4 text-blue-600" />
                        <span>At risk</span>
                      </div>
                    </td>
                    {cohort.atRisk.map((count, idx) => (
                      <td
                        key={idx}
                        className={`px-3 py-2 text-center font-mono ${count === null ? 'text-slate-300' : 'font-semibold text-slate-800'}`}
                      >
                        {count === null ? '-' : count}
                      </td>
                    ))}
                  </tr>

                  {/* Censored Row */}
                  <tr className="border-b border-slate-100 hover:bg-amber-50 transition-colors">
                    <td className="px-4 py-2 text-slate-700 font-medium sticky left-0 bg-white hover:bg-amber-50 z-10">
                      <div className="flex items-center gap-2">
                        <XCircle className="w-4 h-4 text-amber-600" />
                        <span>Censored</span>
                      </div>
                    </td>
                    {cohort.censored.map((count, idx) => (
                      <td
                        key={idx}
                        className={`px-3 py-2 text-center font-mono ${count === null ? 'text-slate-300' : 'text-slate-600'}`}
                      >
                        {count === null ? '-' : count}
                      </td>
                    ))}
                  </tr>

                  {/* Events Row */}
                  <tr className="border-b border-slate-200 hover:bg-red-50 transition-colors">
                    <td className="px-4 py-2 text-slate-700 font-medium sticky left-0 bg-white hover:bg-red-50 z-10">
                      <div className="flex items-center gap-2">
                        <Activity className="w-4 h-4 text-red-600" />
                        <span>Events</span>
                      </div>
                    </td>
                    {cohort.events.map((count, idx) => (
                      <td
                        key={idx}
                        className={`px-3 py-2 text-center font-mono ${count === null ? 'text-slate-300' : 'text-slate-600'}`}
                      >
                        {count === null ? '-' : count}
                      </td>
                    ))}
                  </tr>
                </React.Fragment>
              ))}
            </tbody>

            {/* Footer - Time Unit Label (only shown when lines of failure are active) */}
            {showTimeFooter && (
            <tfoot>
              <tr className="bg-slate-50 border-t-2 border-slate-300">
                <td className="px-4 py-3 text-xs font-bold text-slate-600 uppercase sticky left-0 bg-slate-50 z-10">
                  Time ({timeUnit})
                </td>
                {timePoints.map((time, idx) => (
                  <td
                    key={idx}
                    className="px-3 py-3 text-center text-xs font-bold text-slate-600"
                  >
                    {time}
                  </td>
                ))}
              </tr>
            </tfoot>
            )}
          </table>
        </div>

        {/* Info Footer */}
        <div className="bg-slate-50 px-4 py-3 border-t border-slate-200">
          <div className="flex flex-wrap items-center gap-4 text-xs text-slate-600">
            <div className="flex items-center gap-2">
              <Users className="w-4 h-4 text-blue-600" />
              <span><strong>At risk:</strong> Patients still being followed</span>
            </div>
            <div className="flex items-center gap-2">
              <XCircle className="w-4 h-4 text-amber-600" />
              <span><strong>Censored:</strong> Not reached the event time</span>
            </div>
            <div className="flex items-center gap-2">
              <Activity className="w-4 h-4 text-red-600" />
              <span><strong>Events:</strong> Deaths/progressions (cumulative)</span>
            </div>
          </div>
        </div>

      </div>
    </div>
  );
};

export default RiskTable;
